/**
 * @voltxampere/spice — LED codec (D variant with LED model)
 *
 * Berkeley SPICE3 syntax: `D{name} N+ N- LED_MODEL`
 *
 * Examples:
 *   D1 anode cathode LEDR
 *   D2 a b LEDG_HIGH
 *
 * Recognised LED model markers (case-insensitive):
 *   LEDR  → red
 *   LEDG  → green
 *   LEDB  → blue
 *   LEDY  → yellow
 *   LEDW  → white
 *   anything containing the literal `LED` substring → routed here
 *
 * Schema mapping: `type: "led"`, params: `{ model, color }`. Color
 * is derived from the model name suffix; defaults to `red` for
 * unknown LED models.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { stringParam } from "./_param-helpers.js";
const LED_COLOR_MAP = {
    LEDR: "red",
    LEDG: "green",
    LEDB: "blue",
    LEDY: "yellow",
    LEDW: "white",
};
function colorFromModel(model) {
    const upper = model.toUpperCase();
    for (const [marker, color] of Object.entries(LED_COLOR_MAP)) {
        if (upper.startsWith(marker))
            return color;
    }
    return "red";
}
export const ledCodec = {
    spiceLetter: "D",
    schemaTypes: ["led"],
    examples: ["D1 anode cathode LEDR", "D2 a b LEDG_HIGH"],
    parse(card, _ctx) {
        if (card.tokens.length < 3) {
            return err("LED needs 2 nodes and a model name");
        }
        const [na, nb, modelRaw] = card.tokens;
        if (na === undefined || nb === undefined || modelRaw === undefined) {
            return err("LED has incomplete tokens");
        }
        if (!modelRaw.toUpperCase().includes("LED")) {
            return err("Not an LED model");
        }
        const params = {
            model: modelRaw,
            color: colorFromModel(modelRaw),
        };
        const component = {
            id: card.identifier,
            type: "led",
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        return ok(component, [na, nb]);
    },
    emit(component, terminals) {
        const model = stringParam(component.params.model, "LEDR");
        const [a, b] = terminals;
        return `${component.id} ${a ?? "0"} ${b ?? "0"} ${model}`;
    },
    owns(schemaType) {
        return schemaType === "led";
    },
};
//# sourceMappingURL=led.js.map